import React, { useEffect, useRef, useState } from 'react'
import SVG from 'react-inlinesvg'
import { GeneratorControlButtonDiv } from './common'
import { Motif } from '../../types/motifs'
import MotifListItem, { RemoveIcon } from './MotifListItem'
import { useContextSelectedMotifs } from '../../contexts/selectedMotifContext'
import ConfirmationDialog from '../ConfirmationDialog'
import { useContextPanels } from '../../contexts/panelsContext'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { UIElement } from '../../types/UIElements'
import { useContextMotifGenerator } from '../../contexts/motifGeneratorContext'
import { MotifGeneratorType } from '../MotifPatternGenerator/types'
import { getPagePositionXY } from '../../utils/getPagePositionXY'
import { isMobile } from 'react-device-detect'
import { useLocation } from 'react-router-dom'
import { useContextScreensaver } from '../../contexts/screensaverContext'
import { useThrottleFn } from '@react-cmpt/use-throttle'
import { useContextMotifDetails } from '../../contexts/motifDetailsContext'
import { cn } from '../../utils/cn'

const MotifListControl = () => {
  const { setDetailsOpen, setTargetedMotif } = useContextMotifDetails()
  const { panelState, isNothingOpen, isPanelOpen } = useContextPanels()
  const shouldBeOpen =
    isNothingOpen || isPanelOpen(UIElement.CONTROLS) || isPanelOpen(UIElement.GEOFILTER)
  const { setIsScreensaverOn } = useContextScreensaver()
  const { selectedGeneratorType, setSelectedMotifForFreeGeneration } = useContextMotifGenerator()
  const { setFreeGeneratorTouchEventPageXY } = useContextMotifGenerator()
  const { selectedMotifs, setSelectedMotifs, deselectMotif } = useContextSelectedMotifs()
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [motifToRemove, setMotifToRemove] = useState<Motif | undefined>()
  const containerWrapperRef = useRef<HTMLDivElement>(null)
  const firstButtonRef = useRef<HTMLDivElement>(null)
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth)
  const [isOpen, setIsOpen] = useState<boolean>(shouldBeOpen)
  const [containerHeight, setContainerHeight] = useState<number>()
  const [gap, setGap] = useState<number>(0)
  const location = useLocation()
  const shouldDragMotifOut = useRef(false)

  const dragState = useRef({ isDragging: false, startX: 0, startY: 0 })

  useEffect(() => {
    if (shouldBeOpen) {
      setIsOpen(true)
    } else if (isOpen && !isPanelOpen(UIElement.SIDEMENU) && !isPanelOpen(UIElement.EXPLANATION)) {
      setIsOpen(false)
    }
  }, [panelState])

  const handleResize = () => {
    setWindowWidth(window.innerWidth)
  }

  useEffect(() => {
    window.addEventListener('resize', callback)

    return () => window.removeEventListener('resize', callback)
  }, [])

  const { callback } = useThrottleFn(handleResize, 40)

  useEffect(() => {
    if (!containerWrapperRef.current) return
    const outerContainerHeight = containerWrapperRef.current.getBoundingClientRect().height
    setContainerHeight(outerContainerHeight)
  }, [windowWidth, selectedMotifs.length])

  useEffect(() => {
    if (
      containerHeight &&
      firstButtonRef.current &&
      !(isMobile && window.matchMedia('(orientation: portrait)').matches)
    ) {
      setGap((containerHeight - 4 * firstButtonRef.current.closest('div')!.offsetHeight) / 3)
    }
  }, [containerHeight, firstButtonRef])

  const handleClickRemove = (id: number) => {
    const motif = selectedMotifs.find(m => m.id === id)
    if (!motif) return
    setMotifToRemove(motif)
    setShowConfirmation(true)
    setDetailsOpen(false)
  }

  const handleClickInfo = (id: number) => {
    if (selectedGeneratorType === MotifGeneratorType.FREE && dragState.current.isDragging) return
    const motif = selectedMotifs.find(m => m.id === id)
    if (!motif) return
    shouldDragMotifOut.current = false
    setSelectedMotifForFreeGeneration(undefined)
    setDetailsOpen(false)
    setTargetedMotif(id)
  }

  const handleRemoveConfirm = (confirmed: boolean) => {
    if (confirmed && motifToRemove) deselectMotif(motifToRemove.id)
    setShowConfirmation(false)
    setMotifToRemove(undefined)
  }

  const onDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) return

    const items = reorder(selectedMotifs, result.source.index, result.destination.index)

    setSelectedMotifs(items)
  }

  // a little function to help us with reordering the result
  const reorder = (list: Motif[], startIndex: number, endIndex: number) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  const getPoint = (event: React.MouseEvent | React.TouchEvent) => {
    if ('touches' in event && event.touches.length > 0) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY }
    }
    const e = event as React.MouseEvent
    return { x: e.clientX, y: e.clientY }
  }

  const onMouseDownHandler = (event: React.MouseEvent | React.TouchEvent, index: number) => {
    if (selectedGeneratorType === MotifGeneratorType.FREE) {
      setFreeGeneratorTouchEventPageXY(getPagePositionXY(event))

      const point = getPoint(event)
      dragState.current = { isDragging: false, startX: point.x, startY: point.y }
      shouldDragMotifOut.current = true
      setTimeout(() => {
        if (shouldDragMotifOut.current) {
          setSelectedMotifForFreeGeneration(selectedMotifs[index])
        }
      }, 115)
    }
  }

  const onTouchMoveHandler = (event: React.TouchEvent) => {
    const point = getPoint(event)
    const dx = Math.abs(point.x - dragState.current.startX)
    const dy = Math.abs(point.y - dragState.current.startY)
    if (dx > 5 || dy > 5) {
      dragState.current.isDragging = true
      setFreeGeneratorTouchEventPageXY(getPagePositionXY(event))
    }
  }

  const onMouseUpHandler = (event: React.MouseEvent | React.TouchEvent) => {
    if (selectedGeneratorType !== MotifGeneratorType.FREE) return
    if (!dragState.current.isDragging) return

    if (event.type === 'touchend') event.preventDefault()

    shouldDragMotifOut.current = false
    setSelectedMotifForFreeGeneration(undefined)
  }

  return (
    <>
      <div
        className={cn(
          'absolute left-[-200px] top-1/2 z-10 mt-[-100px] h-[200px] [transition:0.2s_left_ease-out] tall-xxs:mt-[-120px] tall-xxs:h-[240px] sm:mt-[-130px] sm:h-[260px] md:mt-[-160px] md:h-[320px] xl:mt-[-207.5px] xl:h-[415px] [&.open]:left-0 xxl:[&.open]:left-[3vw]',
          { open: isOpen },
        )}
      >
        <div className="h-full rounded-br-[17px] rounded-tr-[17px] bg-bgLightGrey px-[12px] py-[10px] shadow-[0_2px_4px_0_rgba(0,0,0,0.2),0_4px_10px_0_rgba(0,0,0,0.2)] sm:p-[15px] md:p-[18px] xl:p-[21px] xxl:rounded-bl-[17px] xxl:rounded-tl-[17px]">
          <div className="flex h-full flex-col" ref={containerWrapperRef}>
            {selectedGeneratorType === MotifGeneratorType.FREE ? (
              <div className="flex h-full flex-col">
                {new Array(selectedMotifs.length).fill(null).map((_, index) => {
                  let isPlusOne = index === selectedMotifs.length
                  let isEmpty = index > selectedMotifs.length
                  return (
                    <div
                      key={index}
                      className={cn(
                        '[--button-size:40px] tall-xxs:[--button-size:50px] md:[--button-size:60px] xl:[--button-size:80px]',
                        `btn-${index}`,
                      )}
                      ref={index === 0 ? firstButtonRef : null}
                      style={{
                        marginBottom: `${index !== 3 ? gap : 0}px`,
                      }}
                      onTouchStart={event => onMouseDownHandler(event, index)}
                      onMouseDown={event => onMouseDownHandler(event, index)}
                      onMouseUp={event => onMouseUpHandler(event)}
                      onTouchEnd={event => onMouseUpHandler(event)}
                      onTouchMove={onTouchMoveHandler}
                      onClick={() => {
                        setIsScreensaverOn(false)
                        handleClickInfo(selectedMotifs[index].id)
                      }}
                    >
                      <MotifListItem
                        useSpacing={true}
                        motif={selectedMotifs[index]}
                        index={index}
                        isEmpty={isEmpty}
                        isPlusOne={isPlusOne}
                        onRemove={handleClickRemove}
                      />
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex h-full flex-col">
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="droppable" direction="vertical">
                    {provided => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {new Array(selectedMotifs.length).fill(null).map((_, index) => (
                          <div
                            key={index}
                            ref={index === 0 ? firstButtonRef : null}
                            className={`btn-${index}`}
                            style={{
                              marginBottom: `${index !== 3 ? gap : 0}px`,
                            }}
                          >
                            <Draggable key={index} draggableId={`draggable-${index}`} index={index}>
                              {provided => {
                                return (
                                  <GeneratorControlButtonDiv
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={{
                                      backgroundImage: `url(${
                                        selectedMotifs[index].vectorizedUrlXs ||
                                        selectedMotifs[index].vectorizedUrl ||
                                        ''
                                      })`,
                                      ...provided.draggableProps.style,
                                    }}
                                    onClick={() => {
                                      setIsScreensaverOn(false)
                                      handleClickInfo(selectedMotifs[index].id)
                                    }}
                                  >
                                    <RemoveIcon
                                      data-rfd-drag-handle-context-id={
                                        provided.dragHandleProps?.[
                                          'data-rfd-drag-handle-context-id'
                                        ]
                                      }
                                      data-rfd-drag-handle-draggable-id="any-id-to-avoid-drag-event-from-being-fired"
                                      onClick={e => {
                                        e.stopPropagation()
                                        setIsScreensaverOn(false)
                                        handleClickRemove(selectedMotifs[index].id)
                                      }}
                                    >
                                      <SVG src="/icons/x_red.svg" width={12} height={12} />
                                    </RemoveIcon>
                                  </GeneratorControlButtonDiv>
                                )
                              }}
                            </Draggable>
                          </div>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            )}
            {selectedMotifs.length !== 4 && (
              <div className="flex h-full flex-col">
                {new Array(4 - selectedMotifs.length).fill(null).map((_, index) => {
                  let isPlusOne = index === 0 && location.pathname === '/generate'
                  let isEmpty = !isPlusOne
                  return (
                    <div
                      key={index}
                      style={{ marginBottom: `${index !== 4 - selectedMotifs.length ? gap : 0}px` }}
                    >
                      <MotifListItem
                        useSpacing={true}
                        index={index}
                        isEmpty={isEmpty}
                        isPlusOne={isPlusOne}
                        onRemove={handleClickRemove}
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      {showConfirmation && (
        <ConfirmationDialog onConfirm={handleRemoveConfirm} message={'motifDeselectConfirmation'} />
      )}
    </>
  )
}

export default MotifListControl
